<template>
  <div class="contact-page">
    <!-- En-tête avec dégradé, sans la navigation en double -->
    <header class="header">
      <div class="header-content">
        <h1 class="main-title">Contactez-nous</h1>
        <p class="subtitle">Notre équipe est à votre disposition pour répondre à toutes vos questions.</p>
      </div>
    </header>

    <div class="content-container">
      <!-- Introduction -->
      <section class="card intro-card">
        <div class="card-content">
          <h2 class="section-title">Envoyez-nous un message !</h2>
          <div class="text-content">
            <p>
              Si vous souhaitez recevoir un devis pour nos services, veuillez inclure autant de détails que possible dans votre message. 
              De cette façon, nous pourrons mieux comprendre vos besoins et vous proposer une offre adaptée à votre situation.
            </p>
            
            <p>
              N'hésitez pas à nous poser toutes les questions supplémentaires que vous pourriez avoir concernant nos produits, 
              services ou toute autre demande. Nous sommes là pour vous fournir les informations dont vous avez besoin.
            </p>
            
            <p>
              Nous sommes également ouverts à la discussion ! Si vous souhaitez simplement échanger des idées, partager vos 
              commentaires ou discuter de sujets en lien avec la digitalisation de votre entreprise, nous serons ravis de lire vos messages et d'engager une conversation 
              avec vous.
            </p>
          </div>
        </div>
      </section>
      
      <!-- Rendez-vous -->
      <section class="card why-choose-card">
        <div class="card-content">
          <h2 class="section-title">Prenez un rendez-vous téléphonique / vidéo</h2>
          <div class="cta-container">
            <span class="cta-button fake-link" @click="navigateTo('e1')">Votre rendez-vous personnalisé dès maintenant</span>
          </div>
        </div>
      </section>
      
      <!-- Formulaire -->
      <section class="card form-card">
        <div class="card-content">
          <h2 class="section-title">Ou par le formulaire de contact ci-dessous</h2>
          
          <form @submit.prevent="submitForm" class="contact-form">
            <div class="form-group">
              <label for="nom">Nom</label>
              <input type="text" id="nom" v-model="formData.nom" placeholder="Votre nom *" required>
            </div>
            
            <div class="form-group">
              <label for="email">E-mail</label>
              <input type="email" id="email" v-model="formData.email" placeholder="Votre adresse e-mail *" required>
            </div>
            
            <div class="form-group">
              <label for="sujet">Sujet</label>
              <input type="text" id="sujet" v-model="formData.sujet" placeholder="Sujet *" required>
            </div>
            
            <div class="form-group">
              <label for="message">Message</label>
              <textarea id="message" v-model="formData.message" placeholder="Votre message *" rows="6" required></textarea>
            </div>
            
            <div class="form-group">
              <PuzzleCaptcha 
                :puzzle-image="puzzleImage" 
                ref="puzzleCaptcha"
                @verification-change="onVerificationChange"
              />
            </div>
            
            <p class="form-note">* Champs obligatoires</p>
            <p v-if="successMessage" class="form-success">{{ successMessage }}</p>
            
            <div class="cta-container">
              <button type="submit" class="cta-button">Envoyer</button>
            </div>
          </form>
          
          <p class="privacy-notice">
            En remplissant ce formulaire, vous acceptez qu'Arynae LLC soit en possession de votre adresse e-mail et des
            informations transmises dans ce message durant nos futurs échanges.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head'
import axios from 'axios'
import PuzzleCaptcha from '@/components/common/PuzzleCaptcha.vue'

export default {
  name: 'ContactView',
  components: {
    PuzzleCaptcha
  },
  setup() {
    useHead({
      title: 'Contact - Demande d\'information et devis | Arynae',
      meta: [
        { name: 'description', content: 'Contactez Arynae pour discuter de vos projets digitaux. Demandez un devis gratuit pour nos services de SEO, création de SaaS, chatbots ou solutions No Code.' },
        { name: 'keywords', content: 'contact Arynae, demande devis, agence digitale, contact SEO, contact développement SaaS, devis chatbot, devis no code' },
        { name: 'robots', content: 'index, nofollow' },
        { name: 'author', content: 'Arynae' },
        { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
        // Open Graph essentiels
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: 'Contact - Demande d\'information et devis | Arynae' },
        { property: 'og:description', content: 'Contactez Arynae pour discuter de vos projets digitaux. Demandez un devis gratuit pour nos services de SEO, création de SaaS, chatbots ou solutions No Code.' },
        { property: 'og:url', content: 'https://arynae.com/contact' },
        { property: 'og:locale', content: 'fr_FR' },
        { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
        { property: 'og:image:alt', content:'Agence digitale - Solutions No Code, SaaS, Chatbots & SEO' },
        // Twitter Card basiques
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Contact - Demande d\'information et devis | Arynae' },
        { name: 'twitter:description', content: 'Contactez Arynae pour discuter de vos projets digitaux. Demandez un devis gratuit pour nos services de SEO, création de SaaS, chatbots ou solutions No Code.' },
        { name: 'twitter:image', content: 'https://arynae.com/img/arynae-social-card.png' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
      ],
      link: [
        { rel: 'canonical', href: 'https://arynae.com/contact' },
        { rel: 'alternate', hreflang: 'fr', href: 'https://arynae.com/contact' }
      ],
      htmlAttrs: {
        lang: 'fr'
      }
    })
    
    return {}
  },
  data() {
  return {
    formData: {
      nom: '',
      email: '',
      sujet: '',
      message: '',
    },
    puzzleImage: '/img/puzzle-captcha.jpg',
    puzzleVerified: false,
    successMessage: ''
  }
},
  methods: {
    navigateTo(code) {
      const routes = {
        'e1': 'https://cal.com/web-passion/rdv'
      };
      
      window.open(routes[code] || code, '_blank');
    },
    
    onVerificationChange(isValid) {
      this.puzzleVerified = isValid;
    },
    
    async submitForm() {
      // Vérifier si le puzzle a été complété
      if (!this.puzzleVerified) {
        alert('Veuillez compléter le puzzle de vérification');
        return;
      }

      try {
        // Récupérer la position de la pièce depuis le champ caché
        const puzzlePiecePosition = document.querySelector('[name="puzzle_piece_position"]').value;

        // Préparation des données pour l'envoi
        const payload = {
          nom: this.formData.nom,
          email: this.formData.email,
          sujet: this.formData.sujet,
          message: this.formData.message,
          remarque: 'atHUiopfgKuotG45sd',
          puzzle_piece_position: puzzlePiecePosition
        }

        // Envoi du formulaire
        const response = await axios.post('/d7NrautX1TjB8aG6xR9xjECh4.php', payload, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        // Réinitialisation du formulaire après envoi
        this.formData = {
          nom: '',
          email: '',
          sujet: '',
          message: ''
        };
        
        // Réinitialiser l'état du puzzle
        this.puzzleVerified = false;

        // Message de succès affiché sur la page (non bloquant)
        this.successMessage = (response.data && response.data.message) || 'Votre message a été envoyé avec succès !';

        // Redirection après le délai annoncé par le serveur (5 secondes réelles)
        if (response.data && response.data.redirect && response.data.redirect.url) {
          const delay = response.data.redirect.delay || 5000;
          setTimeout(() => {
            window.location.replace(response.data.redirect.url);
          }, delay);
        }
        
      } catch (error) {
        // Gestion des erreurs
        alert(error.response?.data?.message || 'Une erreur est survenue lors de l\'envoi du formulaire');
        // Session puzzle consommée : charger un captcha neuf pour une nouvelle tentative
        this.$refs.puzzleCaptcha.reload();
      }
    }
  }
  }
</script>

<style scoped>
.form-success {
  background-color: #dcfce7;
  color: #166534;
  padding: 1rem;
  border-radius: 0.5rem;
  text-align: center;
}

.contact-page {
  font-family: Arial, sans-serif;
  color: #333;
}

/* Header Styles */
.header {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  padding-bottom: 6rem;
  color: white;
}

.header-content {
  text-align: center;
  padding: 5rem 2rem 3rem;
}

.main-title {
  font-size: 2.75rem;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
}

/* Content Container */
.content-container {
  max-width: 1200px;
  margin: -3rem auto 4rem;
  padding: 0 2rem;
}

/* Card Styles */
.card {
  background: linear-gradient(to bottom, #ffffff, #f3f4f6);
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  margin-bottom: 2rem;
  overflow: hidden;
}

.card-content {
  padding: 2rem;
}

.section-title {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: #333;
  display: block;
  text-align: center;
}

.text-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
}

/* Call to Action */
.cta-container {
  text-align: center;
  margin: 2rem 0 1rem;
}

.cta-button {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  color: white;
  font-size: 1.25rem;
  font-weight: bold;
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 2rem;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-decoration: none;
  display: inline-block;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
}

/* Form Styles */
.contact-form {
  max-width: 800px;
  margin: 0 auto;
}

.form-group {
  margin-bottom: 1.5rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

input, textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

input:focus, textarea:focus {
  outline: none;
  border-color: #2563EB;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.form-note {
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  font-style: italic;
  color: #666;
  text-align: center;
}

.privacy-notice {
  font-size: 0.875rem;
  color: #666;
  margin-top: 2rem;
  text-align: center;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .header-content {
    padding: 4rem 1rem 2rem;
  }
  
  .main-title {
    font-size: 2rem;
  }
  
  .content-container {
    padding: 0 1rem;
  }
  
  .card-content {
    padding: 1.5rem;
  }
  
  .cta-button {
    font-size: 1rem;
    padding: 0.75rem 1.5rem;
  }
}
</style>